import { useMemo, useState, type ReactNode } from 'react'
import { useAuth } from '@/lib/auth/use-auth'
import { useReference } from '@/lib/reference/use-reference'
import { GROUP_CODE_MAX, type AirportGroup, type CountryGroup } from '@/lib/reference'
import { airportGroupIndex, countryGroupIndex } from '@/lib/reference/groups'
import { ReferenceTable, type Column } from '@/components/reference/ReferenceTable'

type Tab = 'airport' | 'country'

/** Row shape shared by both tables — the two group tables are one shape. */
type GroupRow = AirportGroup | CountryGroup

/**
 * Named sets of stations and of countries.
 *
 * DOM, ITL and LIMITED were tabs on the airport screen; they are groups, and
 * so is anything the operator names here. The point is to say "these
 * eighteen stations" once: a rule written against a group keeps covering its
 * members as they change, where eighteen station rules drift apart.
 *
 * Built-in groups are **derived** — DOM reads the scope column, ASIA reads
 * the continent column — so their membership cannot fall out of step with
 * the tables, and they are locked here rather than editable into a no-op.
 * A group the operator adds is an explicit list.
 */
export function GroupsPage() {
  const { can } = useAuth()
  const {
    airports,
    countries,
    airportCapability,
    airportGroups,
    countryGroups,
    create,
    update,
    remove,
  } = useReference()
  const [tab, setTab] = useState<Tab>('airport')

  const editable = can('settings.edit')

  const airportIndex = useMemo(
    () => airportGroupIndex(airportGroups, airports, airportCapability),
    [airportGroups, airports, airportCapability],
  )
  const countryIndex = useMemo(
    () => countryGroupIndex(countryGroups, countries, airports),
    [countryGroups, countries, airports],
  )

  const knownStations = useMemo(
    () => new Set(airports.map((row) => row.iata).filter(Boolean)),
    [airports],
  )
  const knownCountries = useMemo(() => new Set(countries.map((row) => row.code)), [countries])

  const index = tab === 'airport' ? airportIndex : countryIndex
  const rows: GroupRow[] = tab === 'airport' ? airportGroups : countryGroups
  const table = tab === 'airport' ? 'airportGroup' : 'countryGroup'

  /** A list group naming something no table has is a rule that never fires. */
  function issueOf(row: GroupRow): string | null {
    if (row.rule) return index.get(row.code)?.size ? null : 'Derivation matches nothing'
    const known = tab === 'airport' ? knownStations : knownCountries
    const unknown = row.members.filter((code) => !known.has(code))
    if (unknown.length) return `Not in table: ${unknown.slice(0, 6).join(' ')}`
    if (row.members.length === 0) return 'No members'
    return null
  }

  const columns = useMemo<Column<GroupRow>[]>(
    () => [
      {
        key: 'code',
        header: 'Code',
        value: (row) => row.code,
        editor: 'text',
        isKey: true,
        maxLength: GROUP_CODE_MAX,
        cellClassName: 'w-28',
        render: (row) => <span className="t-number-sm text-ink">{row.code}</span>,
      },
      { key: 'name', header: 'Name', value: (row) => row.name, editor: 'text' },
      {
        key: 'rule',
        header: 'Membership',
        value: (row) => row.rule || 'list',
        filter: 'select',
        cellClassName: 'w-44',
        render: (row) =>
          row.rule ? (
            <span className="flex items-center gap-2">
              <span className="t-number-sm text-body">{row.rule}</span>
              <span className="t-caption-strong rounded-[100px] bg-surface-strong px-2 py-0.5 text-muted">
                Derived
              </span>
            </span>
          ) : (
            <span className="t-caption text-muted">list</span>
          ),
      },
      {
        key: 'size',
        header: tab === 'airport' ? 'Stations' : 'Countries',
        value: (row) => index.get(row.code)?.size ?? 0,
        numeric: true,
        filter: 'none',
        cellClassName: 'w-24 text-right',
      },
      {
        // Editable only in the form: a derived group ignores it, and the
        // locked row keeps it out of reach there.
        key: 'members',
        header: 'Members',
        value: (row) => row.members.join(' '),
        editor: 'text',
        edit: (row) => row.members.join(', '),
        cellClassName: 'min-w-[220px]',
        render: (row) => {
          const members = [...(index.get(row.code) ?? [])].sort()
          if (members.length === 0) return <span className="text-muted-soft">—</span>
          const shown = members.slice(0, 8)
          return (
            <span className="t-number-sm text-body">
              {shown.join(' ')}
              {members.length > shown.length && (
                <span className="t-caption text-muted"> +{members.length - shown.length}</span>
              )}
            </span>
          )
        },
      },
    ],
    [tab, index],
  )

  return (
    <section>
      <h1 className="t-title-lg text-ink">Groups</h1>

      <div className="mt-6">
        <ReferenceTable
          key={tab}
          columns={columns}
          rows={rows}
          entity={tab === 'airport' ? 'Airport group' : 'Country group'}
          canEdit={editable}
          rowWarning={issueOf}
          rowLocked={(row) => row.builtin}
          toolbar={
            <div className="flex rounded-[100px] bg-surface-strong p-1">
              <TabButton active={tab === 'airport'} onClick={() => setTab('airport')}>
                Airports <span className="t-number-sm">{airportGroups.length}</span>
              </TabButton>
              <TabButton active={tab === 'country'} onClick={() => setTab('country')}>
                Countries <span className="t-number-sm">{countryGroups.length}</span>
              </TabButton>
            </div>
          }
          onCreate={(values) =>
            create(table, {
              // Not truncated: the editor caps the length, and validation
              // refuses anything longer rather than renaming it silently.
              code: values.code.trim().toUpperCase(),
              name: values.name.trim(),
              rule: '',
              members: splitMembers(values.members),
              builtin: false,
            })
          }
          onUpdate={(id, values) =>
            update(table, id, {
              name: values.name.trim(),
              members: splitMembers(values.members),
            })
          }
          onDelete={(id) => remove(table, id)}
        />
      </div>
    </section>
  )
}

/** "HAN, SGN DAD" -> ["HAN", "SGN", "DAD"]. Commas or spaces, either way. */
function splitMembers(raw: string | undefined): string[] {
  return [
    ...new Set(
      (raw ?? '')
        .split(/[,\s]+/)
        .map((part) => part.trim().toUpperCase())
        .filter(Boolean),
    ),
  ]
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`t-caption-strong inline-flex items-center gap-1.5 rounded-[100px] px-3.5 py-2
                  transition-colors ${active ? 'bg-canvas text-ink' : 'text-muted hover:text-body'}`}
    >
      {children}
    </button>
  )
}
